import React from "react";
import {
  ARROW_LABEL_FONT_SCALE_MAX,
  ARROW_LABEL_FONT_SCALE_MIN,
  coerceLabelFontScale,
  normalizeLabelRotationDeg,
} from "../../utils/arrowPuzzleGenerator";
import "./ArrowPuzzleLabelSidebar.css";

const BASE_FR = 0.42;
const SCALE_SLIDER_STEP = 0.01;

/**
 * One selected label: font scale (slider, like rotation) + rotation + reset + deselect.
 * @param {{
 *   puzzle: object | null,
 *   cellSize: number,
 *   enabled: boolean,
 *   selectedPathIndex: number | null,
 *   onSelectPath: (index: number | null) => void,
 *   onPathLabelLayoutChange: (pathIndex: number, partial: object) => void,
 *   onResetOnePath: (pathIndex: number) => void,
 * }} props
 */
export default function ArrowPuzzleLabelSidebar({
  puzzle,
  cellSize,
  enabled,
  selectedPathIndex,
  onSelectPath,
  onPathLabelLayoutChange,
  onResetOnePath,
}) {
  const paths = puzzle?.paths;
  const sel = paths?.length && selectedPathIndex != null ? paths[selectedPathIndex] : null;
  const hasSel = Boolean(sel && sel.kind !== "extra" && String(sel.label || "").trim());
  const fontScaleLive = coerceLabelFontScale(sel?.labelFontScale != null ? sel.labelFontScale : 1);

  if (!paths?.length) return null;

  return (
    <aside className="arrow-puzzle-sidebar" aria-label="Selected label controls">
      <div className="arrow-puzzle-sidebar__header">Selected label</div>
      {!enabled && (
        <p className="arrow-puzzle-sidebar__note themeupdate-muted">
          Turn on <strong>Edit labels</strong>, then tap a label on the puzzle to select it.
        </p>
      )}
      {enabled && !hasSel && (
        <p className="arrow-puzzle-sidebar__note themeupdate-muted">Tap a label on the puzzle to select it.</p>
      )}
      {enabled && hasSel && (
        <div className="arrow-puzzle-sidebar__single">
          <div className="arrow-puzzle-sidebar__single-title" title={String(sel.label)}>
            {String(sel.label).length > 28 ? `${String(sel.label).slice(0, 26)}…` : String(sel.label)}
          </div>
          {sel.kind === "correct" || sel.kind === "wrong" ? (
            <span
              className={`arrow-puzzle-sidebar__badge ${
                sel.kind === "correct" ? "arrow-puzzle-sidebar__badge--ok" : "arrow-puzzle-sidebar__badge--bad"
              }`}
            >
              {sel.kind === "correct" ? "Correct" : "Wrong"}
            </span>
          ) : null}

          <label className="arrow-puzzle-sidebar__field">
            <span className="arrow-puzzle-sidebar__label">Label scale</span>
            <div className="arrow-puzzle-sidebar__range-row">
              <input
                type="range"
                className="arrow-puzzle-sidebar__range"
                min={ARROW_LABEL_FONT_SCALE_MIN}
                max={ARROW_LABEL_FONT_SCALE_MAX}
                step={SCALE_SLIDER_STEP}
                value={fontScaleLive}
                onChange={(e) =>
                  onPathLabelLayoutChange(selectedPathIndex, {
                    labelFontScale: coerceLabelFontScale(e.target.value),
                  })
                }
              />
              <span className="arrow-puzzle-sidebar__rot-readout" title="Relative to default label size">
                ×{Number(fontScaleLive.toFixed(2))}
              </span>
            </div>
            <span className="arrow-puzzle-sidebar__hint">
              {ARROW_LABEL_FONT_SCALE_MIN}–{ARROW_LABEL_FONT_SCALE_MAX} · ≈{" "}
              {Math.round(Math.max(0.35, cellSize * BASE_FR * fontScaleLive))} px at this preview cell size
            </span>
          </label>

          <label className="arrow-puzzle-sidebar__field">
            <span className="arrow-puzzle-sidebar__label">Rotation (°)</span>
            <div className="arrow-puzzle-sidebar__range-row">
              <input
                type="range"
                className="arrow-puzzle-sidebar__range"
                min={-180}
                max={180}
                step={1}
                value={normalizeLabelRotationDeg(sel.labelRotationDeg != null ? sel.labelRotationDeg : 0)}
                onChange={(e) =>
                  onPathLabelLayoutChange(selectedPathIndex, {
                    labelRotationDeg: normalizeLabelRotationDeg(e.target.value),
                  })
                }
              />
              <span className="arrow-puzzle-sidebar__rot-readout">
                {normalizeLabelRotationDeg(sel.labelRotationDeg != null ? sel.labelRotationDeg : 0)}°
              </span>
            </div>
          </label>

          <p className="arrow-puzzle-sidebar__hint-inline themeupdate-muted">
            Drag the box on the puzzle to move the label; use this panel for size and rotation (no drag-to-resize on the
            grid).
          </p>

          <div className="arrow-puzzle-sidebar__btn-row">
            <button type="button" className="arrow-puzzle-sidebar__btn" onClick={() => onResetOnePath(selectedPathIndex)}>
              Reset this label
            </button>
            <button type="button" className="arrow-puzzle-sidebar__btn arrow-puzzle-sidebar__btn--ghost" onClick={() => onSelectPath(null)}>
              Deselect
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
